import React, { useEffect, useRef } from "react";
import {Check, Zap, Shield, TrendingUp, BookOpen, Clock, Star, ArrowRight, Sparkles, AlertCircle} from 'lucide-react';

function App() {
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-in");
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(".observe").forEach((el) => {
      observerRef.current?.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* BLOCO 1 - Hero Section (Primeira Dobra) */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-orange-50">
        <div className="absolute inset-0 z-0">
          <img
            src="https://static.lumi.new/c7/c7ab8d8decfbae759117b76fbf2e3a1c.png"
            alt="Hero background"
            className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/80 to-white"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 py-20 text-center">
          {/* Faixa de apoio */}
          <div className="inline-block mb-6 px-6 py-2 bg-gradient-to-r from-emerald-500 to-orange-500 text-white rounded-full text-sm font-semibold shadow-lg">
            <Sparkles className="inline w-4 h-4 mr-2" />
            🌱 Baseado em princípios de nutrição funcional e combinações reais de alimentos
          </div>

          {/* Headline */}
          <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
            <span className="text-gray-900">Você come bem, mas o corpo não responde?</span>
            <br />
            <span className="bg-gradient-to-r from-emerald-600 to-orange-600 bg-clip-text text-transparent text-4xl md:text-5xl">
              O problema pode não ser o alimento — e sim como você combina.
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-xl md:text-2xl text-gray-700 mb-4 max-w-3xl mx-auto leading-relaxed">
            <strong>Um guia simples para melhorar o aproveitamento dos alimentos, aumentar energia e ter resultados mais consistentes</strong>
          </p>
          <p className="text-xl md:text-2xl text-gray-700 mb-8 max-w-3xl mx-auto leading-relaxed">
            sem dieta restritiva e sem contar calorias.
          </p>

          {/* CTA principal */}
          <div className="flex flex-col items-center gap-4 mb-12">
            <button
              onClick={() => {
                if (typeof window !== 'undefined' && (window as any).fbq) {
                  (window as any).fbq('track', 'AddToCart', {
                    content_name: 'Ebook Sinergia Alimentar',
                    content_category: 'Digital Product',
                    value: 19.90,
                    currency: 'BRL'
                  });
                }
                window.location.href = 'https://pay.kiwify.com.br/8M3xARA';
              }}
              className="group px-10 py-5 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-full font-bold text-xl shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center gap-2">
              Quero aprender as combinações
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="text-gray-600 text-sm">
              Acesso imediato • Apenas R$19,90
            </p>
          </div>
        </div>
      </section>

      {/* BLOCO 2 - Segunda Dobra (Identificação / Frustração) */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100 observe opacity-0">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Por que "comer saudável" nem sempre funciona?
            </h2>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              Talvez você já tenha passado por isso:
            </p>

            <div className="space-y-6 mb-8">
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                </div>
                <p className="text-gray-700 text-lg">Come melhor, mas sente pouco resultado</p>
              </div>

              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                </div>
                <p className="text-gray-700 text-lg">Vive alternando dietas que não duram</p>
              </div>

              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                </div>
                <p className="text-gray-700 text-lg">Sente energia instável ao longo do dia</p>
              </div>

              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                </div>
                <p className="text-gray-700 text-lg">Faz tudo "certo", mas o corpo parece travado</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-emerald-50 to-orange-50 rounded-2xl p-6 border-l-4 border-emerald-500">
              <p className="text-lg text-gray-800 mb-4">
                <strong>O problema, na maioria das vezes, não está no alimento isolado.</strong><br />
                Está em como eles interagem dentro do seu corpo.
              </p>
              <p className="text-gray-700">
                Algumas combinações potencializam a absorção de nutrientes.<br />
                Outras simplesmente fazem você desperdiçar boa parte do que consome.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BLOCO 3 - Exemplo Concreto */}
      <section className="py-20 bg-white observe opacity-0">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Um exemplo simples que muda tudo
            </h2>
          </div>

          <div className="bg-gradient-to-br from-emerald-50 via-orange-50 to-yellow-50 rounded-3xl p-10 shadow-xl border-2 border-emerald-200">
            <div className="max-w-4xl mx-auto">
              {/* Visual demonstration with images */}
              <div className="grid md:grid-cols-2 gap-8 mb-10">
                {/* Turmeric */}
                <div className="text-center">
                  <div className="relative mb-4 group">
                    <img 
                      src="https://images.unsplash.com/photo-1615485500834-bc10199bc964?w=400&h=300&fit=crop" 
                      alt="Cúrcuma"
                      className="w-full h-64 object-cover rounded-2xl shadow-lg group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent rounded-2xl flex items-end p-4">
                      <div className="text-white">
                        <h3 className="text-2xl font-bold mb-1">Cúrcuma</h3>
                        <p className="text-sm text-yellow-200">Sozinha: baixa absorção</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Black Pepper */}
                <div className="text-center">
                  <div className="relative mb-4 group">
                    <img 
                      src="https://images.unsplash.com/photo-1599940824399-b87987ceb72a?w=400&h=300&fit=crop" 
                      alt="Pimenta Preta"
                      className="w-full h-64 object-cover rounded-2xl shadow-lg group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent rounded-2xl flex items-end p-4">
                      <div className="text-white">
                        <h3 className="text-2xl font-bold mb-1">Pimenta Preta</h3>
                        <p className="text-sm text-orange-200">O catalisador natural</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Formula visual */}
              <div className="bg-white rounded-2xl p-6 mb-8 shadow-lg">
                <div className="flex items-center justify-center gap-4 flex-wrap">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mb-2 shadow-lg">
                      <span className="text-3xl">🧡</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-700">Cúrcuma</p>
                  </div>
                  
                  <span className="text-4xl font-bold text-emerald-600">+</span>
                  
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-gray-700 to-gray-900 rounded-full flex items-center justify-center mb-2 shadow-lg">
                      <span className="text-3xl">⚫</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-700">Pimenta</p>
                  </div>
                  
                  <span className="text-4xl font-bold text-emerald-600">=</span>
                  
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center mb-2 shadow-lg animate-pulse">
                      <span className="text-3xl">✨</span>
                    </div>
                    <p className="text-sm font-semibold text-emerald-700">2000% mais<br/>absorção</p>
                  </div>
                </div>
              </div>

              <p className="text-xl text-gray-800 mb-6 leading-relaxed text-center">
                <strong>A cúrcuma, sozinha, tem baixa absorção.</strong><br />
                Quando combinada com pimenta-preta, a biodisponibilidade <span className="text-emerald-600 font-bold">aumenta drasticamente.</span>
              </p>

              <p className="text-lg text-gray-700 mb-8 text-center">
                Isso não é exceção.<br />
                É o princípio por trás de dezenas de combinações alimentares que afetam:
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <span className="text-gray-700 font-semibold">energia</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <span className="text-gray-700 font-semibold">saciedade</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <span className="text-gray-700 font-semibold">resposta metabólica</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <span className="text-gray-700 font-semibold">aproveitamento nutricional</span>
                </div>
              </div>

              <div className="text-center pt-6 border-t-2 border-emerald-200">
                <p className="text-2xl font-bold text-gray-900 mb-6">
                  👉 É exatamente isso que você aprende no ebook.
                </p>

                <button
                  onClick={() => {
                    if (typeof window !== 'undefined' && (window as any).fbq) {
                      (window as any).fbq('track', 'AddToCart', {
                        content_name: 'Ebook Sinergia Alimentar',
                        content_category: 'Digital Product',
                        value: 19.90,
                        currency: 'BRL'
                      });
                    }
                    window.location.href = 'https://pay.kiwify.com.br/8M3xARA';
                  }}
                  className="group px-8 py-4 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-full font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center gap-2 mx-auto">
                  Quero aprender as combinações
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <p className="text-gray-600 text-sm mt-3">
                  R$19,90 • Leitura prática e direta
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BLOCO 4 - Transição para o Produto */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white observe opacity-0">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              O Código das Combinações
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Um ebook prático que mostra como combinar alimentos do dia a dia para que o corpo responda melhor —<br />
              <strong>sem radicalismo, sem dieta da moda e sem complicação.</strong>
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border-4 border-emerald-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Você aprende:
            </h3>

            <div className="grid md:grid-cols-3 gap-6 mb-10">
              <div className="text-center p-6 bg-emerald-50 rounded-2xl">
                <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-6 h-6 text-white" />
                </div>
                <p className="text-lg font-semibold text-gray-900">quais combinações ajudam</p>
              </div>

              <div className="text-center p-6 bg-orange-50 rounded-2xl">
                <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
                <p className="text-lg font-semibold text-gray-900">quais atrapalham</p>
              </div>

              <div className="text-center p-6 bg-blue-50 rounded-2xl">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <p className="text-lg font-semibold text-gray-900">e como aplicar isso na rotina real</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ... keep existing code */}

      {/* What's Inside Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-900 to-orange-900 text-white observe opacity-0">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">O Que Está Dentro do Ebook</h2>
            <p className="text-xl text-emerald-100">
              Um arsenal completo de conhecimento científico traduzido em ações práticas
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <BookOpen className="w-10 h-10 text-emerald-300 mb-4" />
              <h3 className="text-xl font-bold mb-3">Mais de 80 Combinações Específicas</h3>
              <p className="text-emerald-100">
                Cada combinação com explicação científica detalhada e proporções exatas para máxima eficácia.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <Star className="w-10 h-10 text-orange-300 mb-4" />
              <h3 className="text-xl font-bold mb-3">Receitas Funcionais Prontas</h3>
              <p className="text-orange-100">
                Smoothies, shots, refeições completas e snacks estratégicos que você pode fazer em minutos.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <Zap className="w-10 h-10 text-yellow-300 mb-4" />
              <h3 className="text-xl font-bold mb-3">Shots de Alto Impacto</h3>
              <p className="text-yellow-100">
                Fórmulas concentradas para momentos específicos: pré-treino, imunidade, energia, recuperação.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <Clock className="w-10 h-10 text-blue-300 mb-4" />
              <h3 className="text-xl font-bold mb-3">Lista de Compras Estratégica</h3>
              <p className="text-blue-100">
                Organize sua despensa com os ingredientes essenciais classificados por objetivo e prioridade.
              </p>
            </div>
          </div>

          <div className="mt-12 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 backdrop-blur-sm rounded-2xl p-8 border-2 border-yellow-400/50">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center">
                <span className="text-2xl">🎁</span>
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-3">BÔNUS EXCLUSIVO</h3>
                <p className="text-lg text-yellow-100">
                  <strong>Guia de Substituições Inteligentes:</strong> Nunca fique travado. Aprenda a adaptar
                  combinações com ingredientes alternativos mantendo a mesma potência sinérgica.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Price Anchor & CTA Section */}
      <section id="cta-section" className="py-20 bg-white observe opacity-0">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-br from-emerald-50 via-orange-50 to-yellow-50 rounded-3xl p-12 shadow-2xl border-4 border-emerald-200">
            <div className="text-center mb-8">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Quanto Vale Transformar Seu Corpo
                <br />
                Em Uma Máquina de Precisão?
              </h2>
              <p className="text-xl text-gray-700 mb-8">
                Uma consulta com nutricionista especializado: <span className="line-through">R$ 300-500</span>
                <br />
                Um curso presencial de nutrição funcional: <span className="line-through">R$ 2.000+</span>
                <br />
                Anos de tentativa e erro desperdiçando dinheiro em dietas:{" "}
                <span className="line-through text-red-600">Inestimável</span>
              </p>

              <div className="mb-8">
                <div className="inline-block bg-gradient-to-r from-emerald-600 to-orange-600 rounded-3xl p-1">
                  <div className="bg-white rounded-3xl px-12 py-8">
                    <p className="text-gray-600 text-lg mb-2">Seu investimento hoje:</p>
                    <div className="flex items-center justify-center gap-3">
                      <span className="text-5xl font-bold text-gray-900">R$</span>
                      <span className="text-7xl font-black bg-gradient-to-r from-emerald-600 to-orange-600 bg-clip-text text-transparent">
                        19,90
                      </span>
                    </div>
                    <p className="text-gray-600 mt-3 text-sm">
                      Menos que um lanche do shopping. Conhecimento para a vida toda.
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => {
                  if (typeof window !== 'undefined' && (window as any).fbq) {
                    (window as any).fbq('track', 'AddToCart', {
                      content_name: 'Ebook Sinergia Alimentar',
                      content_category: 'Digital Product',
                      value: 19.90,
                      currency: 'BRL'
                    });
                  }
                  window.location.href = 'https://pay.kiwify.com.br/8M3xARA';
                }}
                className="group w-full md:w-auto px-12 py-6 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-full font-bold text-2xl shadow-2xl hover:shadow-3xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 mx-auto mb-6 animate-pulse">
                🔥 Quero aprender as combinações
                <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
              </button>

              <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-600" />
                  Acesso imediato
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-600" />
                  Formato digital (PDF)
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-600" />
                  Garantia de 7 dias
                </div>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t-2 border-emerald-200">
              <h3 className="text-2xl font-bold text-center text-gray-900 mb-6">
                🛡️ Garantia Incondicional de 7 Dias
              </h3>
              <p className="text-center text-gray-700 max-w-2xl mx-auto">
                Experimente o ebook completamente <strong>sem riscos</strong>. Se você não ficar 100% satisfeito por
                qualquer motivo, basta enviar um email e devolvemos seu dinheiro. Simples assim.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof - WhatsApp Style */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white observe opacity-0">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">
            O Que Quem Já Decodificou Está Dizendo
          </h2>
          <p className="text-center text-gray-600 mb-12">
            📱 Prints reais de conversas com nossos leitores
          </p>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* WhatsApp Message 1 */}
            <div className="bg-[#E5DDD5] rounded-3xl p-6 shadow-lg" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="https://static.lumi.new/99/996b8c12a0e0920ccaf1d5095f708867.png"
                  alt="Marina Silva"
                  className="w-12 h-12 rounded-full object-cover border-2 border-emerald-300" />
                <div>
                  <p className="font-semibold text-gray-900">Marina Silva</p>
                  <p className="text-xs text-gray-500">Online</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%]">
                  <p className="text-gray-800 text-sm">Gente!! Comprei o ebook ontem e já testei 3 combinações 😱</p>
                  <p className="text-xs text-gray-400 text-right mt-1">14:23</p>
                </div>
                <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%]">
                  <p className="text-gray-800 text-sm">O shot de gengibre + limão + pimenta caiena é INSANO</p>
                  <p className="text-xs text-gray-400 text-right mt-1">14:24</p>
                </div>
                <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%]">
                  <p className="text-gray-800 text-sm">Acordei hoje com MUITA energia e zero inchaço 🔥</p>
                  <p className="text-xs text-gray-400 text-right mt-1">14:24</p>
                </div>
                <div className="bg-[#DCF8C6] rounded-lg rounded-tr-none p-3 shadow-sm max-w-[85%] ml-auto">
                  <p className="text-gray-800 text-sm">Que incrível Marina! E quanto você perdeu até agora?</p>
                  <p className="text-xs text-gray-500 text-right mt-1 flex items-center justify-end gap-1">
                    14:25 <span className="text-blue-500">✓✓</span>
                  </p>
                </div>
                <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%]">
                  <p className="text-gray-800 text-sm">7kg em 6 semanas sem passar fome nenhum dia!!!</p>
                  <p className="text-xs text-gray-400 text-right mt-1">14:26</p>
                </div>
                <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%]">
                  <p className="text-gray-800 text-sm">Melhor investimento de R$19,90 da minha vida 💚</p>
                  <p className="text-xs text-gray-400 text-right mt-1">14:26</p>
                </div>
              </div>
            </div>

            {/* ... keep existing WhatsApp messages 2, 3, 4 ... */}
            <div className="bg-[#E5DDD5] rounded-3xl p-6 shadow-lg" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="https://static.lumi.new/5e/5ed2ab95dd1ac64f8827b65013222a06.png"
                  alt="Carlos Rodrigues"
                  className="w-12 h-12 rounded-full object-cover border-2 border-blue-300" />
                <div>
                  <p className="font-semibold text-gray-900">Carlos Rodrigues</p>
                  <p className="text-xs text-gray-500">Online</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%]">
                  <p className="text-gray-800 text-sm">Cara, só uma coisa</p>
                  <p className="text-xs text-gray-400 text-right mt-1">09:15</p>
                </div>
                <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%]">
                  <p className="text-gray-800 text-sm">POR QUE NINGUÉM ME CONTOU ISSO ANTES??? 😂</p>
                  <p className="text-xs text-gray-400 text-right mt-1">09:15</p>
                </div>
                <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%]">
                  <p className="text-gray-800 text-sm">Eu gastei ANOS fazendo dieta low carb, jejum, contando calorias...</p>
                  <p className="text-xs text-gray-400 text-right mt-1">09:16</p>
                </div>
                <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%]">
                  <p className="text-gray-800 text-sm">E a resposta era só COMBINAR os alimentos certos 🤯</p>
                  <p className="text-xs text-gray-400 text-right mt-1">09:16</p>
                </div>
                <div className="bg-[#DCF8C6] rounded-lg rounded-tr-none p-3 shadow-sm max-w-[85%] ml-auto">
                  <p className="text-gray-800 text-sm">Hahaha a ciência da biodisponibilidade é surreal né?</p>
                  <p className="text-xs text-gray-500 text-right mt-1 flex items-center justify-end gap-1">
                    09:17 <span className="text-blue-500">✓✓</span>
                  </p>
                </div>
                <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%]">
                  <p className="text-gray-800 text-sm">Minha energia TRIPLICOU. Não tomo mais café à tarde</p>
                  <p className="text-xs text-gray-400 text-right mt-1">09:18</p>
                </div>
                <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%]">
                  <p className="text-gray-800 text-sm">E finalmente entendi pq minhas "dietas saudáveis" falhavam 💪</p>
                  <p className="text-xs text-gray-400 text-right mt-1">09:18</p>
                </div>
              </div>
            </div>

            <div className="bg-[#E5DDD5] rounded-3xl p-6 shadow-lg" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="https://static.lumi.new/b8/b8b13f8f651c1e0f08da3950bbcb70ce.png"
                  alt="Ana Paula"
                  className="w-12 h-12 rounded-full object-cover border-2 border-pink-300" />
                <div>
                  <p className="font-semibold text-gray-900">Ana Paula</p>
                  <p className="text-xs text-gray-500">Online</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%]">
                  <p className="text-gray-800 text-sm">Oi! Você que me indicou o ebook de sinergia alimentar?</p>
                  <p className="text-xs text-gray-400 text-right mt-1">16:42</p>
                </div>
                <div className="bg-[#DCF8C6] rounded-lg rounded-tr-none p-3 shadow-sm max-w-[85%] ml-auto">
                  <p className="text-gray-800 text-sm">Sim! Comprou? O que achou?</p>
                  <p className="text-xs text-gray-500 text-right mt-1 flex items-center justify-end gap-1">
                    16:43 <span className="text-blue-500">✓✓</span>
                  </p>
                </div>
                <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%]">
                  <p className="text-gray-800 text-sm">MEUUU DEUSSS 😍😍😍</p>
                  <p className="text-xs text-gray-400 text-right mt-1">16:43</p>
                </div>
                <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%]">
                  <p className="text-gray-800 text-sm">Estou fazendo o shot de cúrcuma + pimenta preta há 5 dias</p>
                  <p className="text-xs text-gray-400 text-right mt-1">16:44</p>
                </div>
                <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%]">
                  <p className="text-gray-800 text-sm">Minha imunidade está NAS ALTURAS e zero resfriado esse inverno ❄️</p>
                  <p className="text-xs text-gray-400 text-right mt-1">16:44</p>
                </div>
                <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%]">
                  <p className="text-gray-800 text-sm">Conhecimento que vale MILHARES por apenas R$19,90</p>
                  <p className="text-xs text-gray-400 text-right mt-1">16:45</p>
                </div>
                <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%]">
                  <p className="text-gray-800 text-sm">Obrigada pela indicação!! 💚💚</p>
                  <p className="text-xs text-gray-400 text-right mt-1">16:45</p>
                </div>
              </div>
            </div>

            <div className="bg-[#E5DDD5] rounded-3xl p-6 shadow-lg" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="https://static.lumi.new/5c/5ce879077f8408103d3db124998316e3.png"
                  alt="Renata Oliveira"
                  className="w-12 h-12 rounded-full object-cover border-2 border-purple-300" />
                <div>
                  <p className="font-semibold text-gray-900">Renata Oliveira</p>
                  <p className="text-xs text-gray-500">Online</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%]">
                  <p className="text-gray-800 text-sm">Preciso contar uma coisa 😊</p>
                  <p className="text-xs text-gray-400 text-right mt-1">20:12</p>
                </div>
                <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%]">
                  <p className="text-gray-800 text-sm">Comprei ceticazinha, pensando "mais um ebook de dieta"</p>
                  <p className="text-xs text-gray-400 text-right mt-1">20:12</p>
                </div>
                <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%]">
                  <p className="text-gray-800 text-sm">Mas isso aqui é CIÊNCIA PURA aplicada na prática</p>
                  <p className="text-xs text-gray-400 text-right mt-1">20:13</p>
                </div>
                <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%]">
                  <p className="text-gray-800 text-sm">As explicações de biodisponibilidade mudaram TUDO</p>
                  <p className="text-xs text-gray-400 text-right mt-1">20:13</p>
                </div>
                <div className="bg-[#DCF8C6] rounded-lg rounded-tr-none p-3 shadow-sm max-w-[85%] ml-auto">
                  <p className="text-gray-800 text-sm">Já testou alguma combinação? 😄</p>
                  <p className="text-xs text-gray-500 text-right mt-1 flex items-center justify-end gap-1">
                    20:14 <span className="text-blue-500">✓✓</span>
                  </p>
                </div>
                <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%]">
                  <p className="text-gray-800 text-sm">Todas!! Hahaha fiz a lista de compras e já enchi a geladeira 🥗</p>
                  <p className="text-xs text-gray-400 text-right mt-1">20:15</p>
                </div>
                <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%]">
                  <p className="text-gray-800 text-sm">Meu corpo virou uma MÁQUINA mesmo. Queimando gordura 24/7 🔥</p>
                  <p className="text-xs text-gray-400 text-right mt-1">20:15</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 text-sm">
              ⚡ Mais de 2.847 pessoas já transformaram seus resultados com Sinergia Alimentar
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-orange-600 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Seu Corpo É Uma Máquina.
            <br />
            Está Na Hora de Aprender o Manual.
          </h2>
          <p className="text-xl mb-8 text-emerald-100">
            Cada dia que passa sem esse conhecimento é um dia a menos transformando sua saúde em alta performance.
          </p>

          <button
            onClick={() => {
              if (typeof window !== 'undefined' && (window as any).fbq) {
                (window as any).fbq('track', 'AddToCart', {
                  content_name: 'Ebook Sinergia Alimentar',
                  content_category: 'Digital Product',
                  value: 19.90,
                  currency: 'BRL'
                });
              }
              window.location.href = 'https://pay.kiwify.com.br/8M3xARA';
            }}
            className="group px-12 py-6 bg-white text-emerald-600 rounded-full font-bold text-2xl shadow-2xl hover:shadow-3xl hover:scale-105 transition-all duration-300 flex items-center gap-3 mx-auto mb-6">
            Quero aprender as combinações
            <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
          </button>

          <p className="text-emerald-100 text-sm">⚡ Acesso imediato após a compra • 🛡️ Garantia de 7 dias</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-gray-900 text-gray-400 text-center">
        <div className="max-w-4xl mx-auto px-6">
          <p className="mb-4">© 2024 Sinergia Alimentar. Todos os direitos reservados.</p>
          <p className="text-sm">
            Este produto não substitui orientação médica ou nutricional profissional. Consulte um especialista antes de
            fazer mudanças significativas em sua dieta.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;